import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import {
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '../config/firebase.config';
import { User, LoginCredentials, RegisterData, AuthState } from '../types/user.types';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';

// Storage key for user data
const USER_STORAGE_KEY = 'pagali_user';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithFacebook: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to save user to localStorage
const saveUserToStorage = (user: User | null) => {
  if (user) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_STORAGE_KEY);
  }
};

// Helper to get user from localStorage
const getUserFromStorage = (): User | null => {
  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading user from storage:', e);
  }
  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(getUserFromStorage());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // First check if we have user data in storage (from login)
          const storedUser = getUserFromStorage();
          if (storedUser && storedUser.id) {
            setUser(storedUser);
          } else {
            // If no stored user, try to get user info from Firebase token claims
            const tokenResult = await firebaseUser.getIdTokenResult();
            const claims = tokenResult.claims;

            if (claims.mysqlId) {
              // We have MySQL user info in claims
              const userData: User = {
                id: claims.mysqlId as string,
                username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || '',
                email: firebaseUser.email || '',
                name: firebaseUser.displayName || '',
                role: (claims.role as string) || 'PFCLI',
                status: 'active',
                createdAt: new Date(),
                lastLogin: new Date(),
                profile: {
                  photoURL: firebaseUser.photoURL || undefined,
                },
              };
              setUser(userData);
              saveUserToStorage(userData);
            } else {
              // No claims, logout
              console.log('No user claims found, logging out');
              await signOut(auth);
              setUser(null);
              saveUserToStorage(null);
            }
          }
        } else {
          setUser(null);
          saveUserToStorage(null);
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError('Failed to authenticate');
        setUser(null);
        saveUserToStorage(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Login with username and password via API
  const login = async (credentials: LoginCredentials) => {
    try {
      setLoading(true);
      setError(null);

      // 1. Call our API to validate credentials in MySQL
      const response = await fetch(`${API_BASE_URL}/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Credenciais inválidas');
      }

      // 2. Create user data from API response BEFORE signing in
      const userData: User = {
        id: data.user.id,
        username: data.user.username,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role,
        status: 'active',
        createdAt: new Date(),
        lastLogin: new Date(),
        profile: {},
        permissions: data.permissions || [],
        entityId: data.user.entidadeId || data.entidadeId || undefined,
      };

      // 3. Save user to storage BEFORE signing in to Firebase
      saveUserToStorage(userData);
      setUser(userData);

      // 4. Sign in to Firebase with custom token
      await signInWithCustomToken(auth, data.token);

      // Don't set loading to false here - let onAuthStateChanged handle it
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      setLoading(false);
      throw new Error(errorMessage);
    }
  };

  // Register new user
  const register = async (data: RegisterData) => {
    try {
      setLoading(true);
      setError(null);

      // Call our API to register the user
      const response = await fetch(`${API_BASE_URL}/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: data.username,
          email: data.email,
          password: data.password,
          name: data.name,
          telefone: data.telefone,
          location: data.location,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Falha no registo');
      }

      // Create user data
      const userData: User = {
        id: result.user.id,
        username: result.user.username,
        email: result.user.email,
        name: result.user.name,
        role: result.user.role,
        status: 'active',
        createdAt: new Date(),
        lastLogin: new Date(),
        profile: {
          location: data.location,
        },
      };

      // Save user to storage
      saveUserToStorage(userData);
      setUser(userData);

      // Sign in to Firebase
      await signInWithCustomToken(auth, result.token);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      setLoading(false);
      throw err;
    }
  };

  // Logout
  const logout = async () => {
    try {
      setLoading(true);
      await signOut(auth);
      setUser(null);
      saveUserToStorage(null);
    } catch (err: any) {
      setError(err.message || 'Logout failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);

      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);

      // Create basic user data from Google
      const userData: User = {
        id: userCredential.user.uid,
        username: userCredential.user.email?.split('@')[0] || '',
        email: userCredential.user.email || '',
        name: userCredential.user.displayName || '',
        role: 'PFCLI',
        status: 'active',
        createdAt: new Date(),
        lastLogin: new Date(),
        profile: {
          photoURL: userCredential.user.photoURL || undefined,
        },
      };

      saveUserToStorage(userData);
      setUser(userData);
    } catch (err: any) {
      setError(err.message || 'Google login failed');
      setLoading(false);
      throw err;
    }
  };

  // Login with Facebook
  const loginWithFacebook = async () => {
    try {
      setLoading(true);
      setError(null);

      const provider = new FacebookAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);

      // Create basic user data from Facebook
      const userData: User = {
        id: userCredential.user.uid,
        username: userCredential.user.email?.split('@')[0] || '',
        email: userCredential.user.email || '',
        name: userCredential.user.displayName || '',
        role: 'PFCLI',
        status: 'active',
        createdAt: new Date(),
        lastLogin: new Date(),
        profile: {
          photoURL: userCredential.user.photoURL || undefined,
        },
      };

      saveUserToStorage(userData);
      setUser(userData);
    } catch (err: any) {
      setError(err.message || 'Facebook login failed');
      setLoading(false);
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err: any) {
      setError(err.message || 'Password reset failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithFacebook,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
